import '@fontsource-variable/inter';
import { api, ApiError } from './lib/api.js';
import { el, render } from './lib/dom.js';
import { setSessionRole, canWrite } from './lib/session.js';
import { icon, avatar, iconButton, badge, closeAllOverlays } from './lib/ui.js';
import { loginView } from './views/login.js';
import { dashboardView } from './views/dashboard.js';
import { orgsView } from './views/orgs.js';
import { orgNewView } from './views/org-new.js';
import { orgDetailView } from './views/org-detail.js';
import { projectsView } from './views/projects.js';
import { trialsView } from './views/trials.js';
import { usersView } from './views/users.js';
import { userDetailView } from './views/user-detail.js';
import './styles.css';
import './admin.css';

// ---------------------------------------------------------------------------
// App shell + hash router.
//
// Hash routing, not history routing: a Static Web App would need a rewrite rule
// for every client-side path, and getting that wrong shows a 404 on refresh.
// Hashes never reach the server.
// ---------------------------------------------------------------------------

const root = document.getElementById('app');
const state = { session: null, meta: null };

function navigate(path) {
  if (window.location.hash === `#${path}`) routeChanged();
  else window.location.hash = path;
}

/**
 * Environment banner. The single most important element on the page: one
 * deployment targets exactly one database, and an operator must never be
 * uncertain which. Production gets the loudest treatment.
 */
function envBanner(label) {
  const isProd = label === 'production';
  const isLocal = label === 'local';
  const text = !canWrite()
    ? `Read-only access to the ${label} database · you can view everything but change nothing.`
    : isProd
      ? 'You are creating, deactivating, and deleting real customer organisations.'
      : isLocal
        ? 'Connected to the local database.'
        : `Connected to the ${label} database · writes are reversible.`;

  return el(
    'div',
    { class: `env-banner env-banner--${isProd ? 'production' : isLocal ? 'local' : 'staging'}`, role: 'note' },
    el('span', { class: 'env-banner__label' }, label.toUpperCase()),
    el('span', {}, text),
  );
}

const NAV = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: 'nav-dashboard', hint: 'Platform overview: organisations, users, projects and recent activity.' },
  { id: 'orgs', label: 'Organisations', path: '/orgs', icon: 'nav-organisations', hint: 'Every tenant organisation: create one, open one to manage its members, trial and limits.' },
  { id: 'projects', label: 'Projects', path: '/projects', icon: 'nav-projects', hint: 'Every project across every organisation, with its schedule versions. Read-only.' },
  { id: 'users', label: 'Users', path: '/users', icon: 'nav-users', hint: 'Every user across every organisation. Account-wide actions live here.' },
  { id: 'trials', label: 'Expiring trials', path: '/trials', icon: 'nav-trials', hint: 'Organisations whose trial ends soon, so you can extend or convert them in time.' },
];

function sidebar(activeNav) {
  const email = state.session?.email ?? '';
  return el(
    'aside',
    { class: 'sidebar' },
    el(
      'div',
      { class: 'sidebar__top' },
      el(
        'button',
        { type: 'button', class: 'brand', title: 'Go to the dashboard.', onclick: () => navigate('/dashboard') },
        el('span', { class: 'brand__mark' }, icon('logo-box')),
        el('span', {}, el('span', { class: 'brand__name', html: 'ConstructMind<em>.ai</em>' }), el('span', { class: 'brand__tag' }, 'ADMIN PORTAL')),
      ),
      el(
        'nav',
        { class: 'nav', 'aria-label': 'Main' },
        NAV.map((item) =>
          el(
            'a',
            {
              href: `#${item.path}`,
              class: `nav-item${activeNav === item.id ? ' is-active' : ''}`,
              'aria-current': activeNav === item.id ? 'page' : null,
              title: item.hint,
            },
            el('span', { class: 'nav-item__icon' }, icon(item.icon)),
            el('span', { class: 'nav-item__label' }, item.label),
          ),
        ),
      ),
    ),
    el(
      'div',
      { class: 'sidebar__footer' },
      avatar(email || 'Admin', { size: 36 }),
      el(
        'div',
        { class: 'sidebar__user' },
        el('span', { class: 'sidebar__user-name', title: email }, email),
        el('span', { class: 'sidebar__user-role' }, canWrite() ? 'Administrator' : 'Read-only viewer'),
      ),
      iconButton('log-out', {
        label: 'Sign out — ends this admin session. Nothing in the data is changed.',
        className: 'sidebar__logout',
        onClick: async () => {
          try {
            await api.logout();
          } finally {
            state.session = null;
            state.meta = null;
            navigate('/login');
          }
        },
      }),
    ),
  );
}

/** Breadcrumb trail; views extend it once they know a record's name. */
function breadcrumb(slot, crumbs) {
  const items = [];
  crumbs.forEach((c, i) => {
    if (i > 0) items.push(el('span', { class: 'breadcrumb__sep' }, icon('breadcrumb-chevron')));
    const last = i === crumbs.length - 1;
    items.push(
      last || !c.path
        ? el('span', { class: last ? 'breadcrumb__current' : '' }, c.label)
        : el('button', { type: 'button', class: 'breadcrumb__link', onclick: () => navigate(c.path) }, c.label),
    );
  });
  render(slot, items);
  const last = crumbs[crumbs.length - 1];
  document.title = last ? `${last.label} · ConstructMind Admin` : 'ConstructMind Admin';
}

function shell(activeNav, baseCrumbs, makeView) {
  const crumbSlot = el('nav', { class: 'breadcrumb', 'aria-label': 'Breadcrumb' });
  const setCrumbs = (extra = []) => breadcrumb(crumbSlot, [...baseCrumbs, ...extra]);
  setCrumbs();

  const content = makeView({ navigate, setCrumbs, meta: state.meta });
  const label = state.session?.envLabel ?? state.meta?.envLabel ?? 'local';

  return el(
    'div',
    { class: 'app' },
    sidebar(activeNav),
    el(
      'div',
      { class: 'main' },
      envBanner(label),
      el(
        'header',
        { class: 'topbar' },
        crumbSlot,
        el(
          'div',
          { class: 'topbar__right' },
          state.meta?.emailConfigured === false
            ? badge('Email off', 'amber', { dot: true, title: 'Azure email is not configured on this deployment. Invites are not sent — join links are shown to copy instead.' })
            : null,
          !canWrite() ? badge('Read-only', 'gray', { dot: true, title: 'Signed in with the read-only login. Every change is refused by the API.' }) : null,
          badge(label.toUpperCase(), label === 'production' ? 'red' : label === 'local' ? 'gray' : 'amber'),
        ),
      ),
      el('main', { class: 'page' }, content),
    ),
  );
}

async function loadMeta() {
  try {
    state.meta = await api.meta();
  } catch {
    // Non-fatal: the UI degrades to not naming the rule snapshot.
    state.meta = null;
  }
}

function showLogin() {
  document.title = 'Sign in · ConstructMind Admin';
  render(
    root,
    loginView({
      onSuccess: async (session) => {
        state.session = session;
        setSessionRole(session.role);
        await loadMeta();
        navigate('/dashboard');
      },
    }),
  );
}

const UUID = '([0-9a-fA-F-]{36})';
const ORGS = { label: 'Organisations', path: '/orgs' };
const USERS = { label: 'Users', path: '/users' };

function routeChanged() {
  closeAllOverlays();
  const hash = window.location.hash.replace(/^#/, '') || '/dashboard';

  if (state.session === null) {
    showLogin();
    return;
  }

  // A hash can carry a query of its own (`#/users?search=…`, `#/orgs/<id>?tab=members`).
  const [path, queryString = ''] = hash.split('?');
  const query = new URLSearchParams(queryString);
  const orgDetail = new RegExp(`^/orgs/${UUID}$`).exec(path);
  const userDetail = new RegExp(`^/users/${UUID}$`).exec(path);

  let view;
  if (path === '/dashboard') {
    view = shell('dashboard', [{ label: 'Dashboard' }], (ctx) => dashboardView(ctx));
  } else if (path === '/orgs/new' && canWrite()) {
    view = shell('orgs', [ORGS, { label: 'New organisation' }], (ctx) => orgNewView(ctx));
  } else if (orgDetail !== null) {
    view = shell('orgs', [ORGS], (ctx) => orgDetailView({ ...ctx, orgId: orgDetail[1], tab: query.get('tab') ?? 'overview' }));
  } else if (path === '/projects') {
    view = shell('projects', [{ label: 'Projects' }], (ctx) => projectsView(ctx));
  } else if (path === '/trials') {
    view = shell('trials', [{ label: 'Expiring trials' }], (ctx) => trialsView(ctx));
  } else if (userDetail !== null) {
    view = shell('users', [USERS], (ctx) => userDetailView({ ...ctx, userId: userDetail[1] }));
  } else if (path === '/users') {
    view = shell('users', [USERS], (ctx) => usersView({ ...ctx, search: query.get('search') ?? '' }));
  } else if (path === '/login') {
    showLogin();
    return;
  } else if (path === '/orgs') {
    view = shell('orgs', [ORGS], (ctx) => orgsView(ctx));
  } else {
    view = shell('dashboard', [{ label: 'Dashboard' }], (ctx) => dashboardView(ctx));
  }
  render(root, view);
  window.scrollTo(0, 0);
}

window.addEventListener('hashchange', routeChanged);

// Boot: probe for an existing session cookie so a refresh does not force a
// re-login while the cookie is still valid.
(async () => {
  render(root, el('div', { class: 'boot' }, 'Loading…'));
  try {
    state.session = await api.me();
    setSessionRole(state.session.role);
    await loadMeta();
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) {
      // A network failure is worth showing rather than silently offering login.
      render(
        root,
        el(
          'div',
          { class: 'boot boot-error' },
          el('p', {}, 'Cannot reach the admin API.'),
          el('p', { class: 'mono' }, err instanceof ApiError ? err.message : String(err)),
        ),
      );
      return;
    }
    state.session = null;
  }
  routeChanged();
})();
